import { Head, Link, router, useForm } from "@inertiajs/react"
import { ArrowLeft, CalendarCheck, ExternalLink, Trash2 } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { CodeBlock } from "@/components/design-system/CodeBlock"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Select } from "@/components/ui/select"
import {
  formatAdminDate,
  LEAD_KIND_LABELS,
  LEAD_STATUS_LABELS,
  type AdminLeadDetail,
  type ConsultationConfirmationSchedule,
  type LeadStatus,
} from "@/types/lead"

const DESCRIPTION = "Review normalized contact data, request context, response timing, lifecycle status, and secondary details for this Lead in Zmile's private admin area."

type Props = {
  lead: AdminLeadDetail
  confirmation_schedule: ConsultationConfirmationSchedule | null
}

type ConfirmationData = {
  consultation_confirmation: {
    date: string
    time: string
    confirmation: string
  }
}

export default function AdminLeadShow({ lead, confirmation_schedule }: Props) {
  const statusForm = useForm<{ status: LeadStatus }>({ status: lead.status })
  const confirmationForm = useForm<ConfirmationData>({
    consultation_confirmation: {
      date: confirmation_schedule?.selected_date || "",
      time: confirmation_schedule?.selected_time || "",
      confirmation: String(lead.id),
    },
  })
  const confirmationErrors = confirmationForm.errors as Record<string, string>
  const selectedDateLabel = confirmation_schedule?.dates.find(
    (date) => date.value === confirmationForm.data.consultation_confirmation.date,
  )?.label

  function updateStatus(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    statusForm.patch(`/admin/leads/${lead.id}`, { preserveScroll: true })
  }

  function deleteLead() {
    router.visit(`/admin/leads/${lead.id}`, {
      data: { confirmation: String(lead.id) },
      method: "delete",
    })
  }

  function confirmConsultation() {
    confirmationForm.post(`/admin/leads/${lead.id}/confirm_consultation`, { preserveScroll: true })
  }

  return (
    <>
      <Head title={`${lead.company_name} · Lead`}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={`${lead.company_name} · Lead`} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <AdminShell>
        <Link className="inline-flex items-center gap-2" href="/admin/leads"><ArrowLeft aria-hidden="true" className="h-4 w-4" />Back to Leads</Link>

        <header className="mt-6 border-b border-hairline pb-6">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={lead.kind === "website_review" ? "accent" : "neutral"}>{LEAD_KIND_LABELS[lead.kind]}</Badge>
            <Badge tone="muted">{LEAD_STATUS_LABELS[lead.status]}</Badge>
            {lead.response_deadline_state === "due" && <Badge tone="signal">Due soon</Badge>}
            {lead.response_deadline_state === "overdue" && <Badge tone="danger">Overdue</Badge>}
          </div>
          <h1 className="mt-3">{lead.company_name}</h1>
          <p className="mt-1">{lead.contact_name} · <a href={`mailto:${lead.email}`}>{lead.email}</a></p>
        </header>

        {confirmation_schedule && (
          <section className="mt-6 rounded-md border border-accent/30 bg-accent-faded p-5" aria-labelledby="confirmation-heading">
            <div className="flex items-center gap-2">
              <CalendarCheck aria-hidden="true" className="h-5 w-5 text-accent-display" />
              <h2 id="confirmation-heading">Confirm Consultation</h2>
            </div>
            <p className="mt-2"><strong>Original requested time:</strong> {formatAdminDate(lead.requested_start_at)}</p>
            <p className="mt-1">Keep the requested time or choose another available 30-minute Zagreb slot.</p>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="confirmed_date">Confirmed date</label>
                <Select
                  id="confirmed_date"
                  onChange={(event) => confirmationForm.setData("consultation_confirmation", {
                    ...confirmationForm.data.consultation_confirmation,
                    date: event.target.value,
                  })}
                  value={confirmationForm.data.consultation_confirmation.date}
                >
                  {confirmation_schedule.dates.map((date) => <option key={date.value} value={date.value}>{date.label}</option>)}
                </Select>
              </div>
              <div>
                <label htmlFor="confirmed_time">Confirmed time</label>
                <Select
                  id="confirmed_time"
                  onChange={(event) => confirmationForm.setData("consultation_confirmation", {
                    ...confirmationForm.data.consultation_confirmation,
                    time: event.target.value,
                  })}
                  value={confirmationForm.data.consultation_confirmation.time}
                >
                  {confirmation_schedule.times.map((time) => <option key={time} value={time}>{time}</option>)}
                </Select>
              </div>
            </div>
            {(confirmationErrors.confirmed_start_at || confirmationErrors.confirmation || confirmationErrors.base) && (
              <p className="form-error mt-2" role="alert">
                {confirmationErrors.confirmed_start_at || confirmationErrors.confirmation || confirmationErrors.base}
              </p>
            )}

            <Dialog>
              <DialogTrigger asChild><Button className="mt-4">Review confirmation</Button></DialogTrigger>
              <DialogContent size="sm">
                <DialogHeader>
                  <DialogTitle>Confirm this Consultation?</DialogTitle>
                  <DialogDescription>
                    Confirm {selectedDateLabel} at {confirmationForm.data.consultation_confirmation.time} Europe/Zagreb and queue the localized email to {lead.email}.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild><Button variant="ghost">Cancel</Button></DialogClose>
                  <Button disabled={confirmationForm.processing} onClick={confirmConsultation}>Confirm and send email</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </section>
        )}

        {lead.kind === "consultation" && lead.confirmed_start_at && (
          <section className="callout callout-accent mt-6" aria-labelledby="confirmed-heading">
            <h2 id="confirmed-heading">Confirmed Consultation</h2>
            <p>{formatAdminDate(lead.confirmed_start_at)}. {confirmationDeliveryMessage(lead)}</p>
          </section>
        )}

        <section className="mt-6 rounded-md border border-hairline bg-surface p-5" aria-labelledby="status-heading">
          <h2 id="status-heading">Lead Status</h2>
          <p className="mt-1">Allowed from {LEAD_STATUS_LABELS[lead.status]}: {lead.allowed_statuses.map((status) => LEAD_STATUS_LABELS[status]).join(", ")}.</p>
          <form className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start" onSubmit={updateStatus}>
            <div className="w-full max-w-xs">
              <label className="sr-only" htmlFor="lead_status">Lead Status</label>
              <Select id="lead_status" onChange={(event) => statusForm.setData("status", event.target.value as LeadStatus)} value={statusForm.data.status}>
                {lead.allowed_statuses.map((status) => <option key={status} value={status}>{LEAD_STATUS_LABELS[status]}</option>)}
              </Select>
              {statusForm.errors.status && <p className="form-error mt-1" role="alert">{statusForm.errors.status}</p>}
            </div>
            <Button disabled={statusForm.processing} type="submit">Update status</Button>
          </form>
        </section>

        <section className="mt-6" aria-labelledby="submission-heading">
          <h2 id="submission-heading">Normalized submission</h2>
          <dl className="mt-4 grid gap-px overflow-hidden rounded-md border border-hairline bg-hairline sm:grid-cols-2">
            <Detail label="Contact name" value={lead.contact_name} />
            <Detail label="Work email" value={lead.email} href={`mailto:${lead.email}`} />
            <Detail label="Company" value={lead.company_name} />
            <Detail label="Locale" value={lead.locale.toUpperCase()} />
            <Detail label="Lead Kind" value={LEAD_KIND_LABELS[lead.kind]} />
            <Detail label="Lead Status" value={LEAD_STATUS_LABELS[lead.status]} />
            <Detail external label="Website" value={lead.website_url || "Not provided"} href={lead.website_url || undefined} />
            <Detail
              external={Boolean(lead.posthog_behavior_url)}
              label="Analytics Identity"
              value={lead.posthog_distinct_id || "Not available"}
              href={lead.posthog_behavior_url || undefined}
            />
            <Detail label="Requested start" value={formatAdminDate(lead.requested_start_at)} />
            <Detail label="Confirmed start" value={formatAdminDate(lead.confirmed_start_at)} />
            <Detail label="Response deadline" value={formatAdminDate(lead.response_deadline)} />
            <Detail label="First response" value={formatAdminDate(lead.responded_at)} />
          </dl>
        </section>

        <section className="mt-6" aria-labelledby="message-heading">
          <h2 id="message-heading">Message</h2>
          <p className="mt-3 whitespace-pre-wrap rounded-md border border-hairline bg-page p-4">{lead.message || "Not provided"}</p>
        </section>

        <section className="mt-6" aria-labelledby="details-heading">
          <h2 id="details-heading">Secondary details</h2>
          <div className="mt-3"><CodeBlock code={JSON.stringify(lead.details, null, 2)} language="json" /></div>
        </section>

        <section className="mt-6" aria-labelledby="timestamps-heading">
          <h2 id="timestamps-heading">Timestamps</h2>
          <dl className="mt-3 grid gap-px overflow-hidden rounded-md border border-hairline bg-hairline sm:grid-cols-2">
            <Detail label="Created" value={formatAdminDate(lead.created_at)} />
            <Detail label="Updated" value={formatAdminDate(lead.updated_at)} />
          </dl>
        </section>

        <section className="mt-10 border-t border-danger/30 pt-6" aria-labelledby="danger-heading">
          <h2 id="danger-heading">Delete Lead</h2>
          <p className="mt-1">This permanently removes only this Lead and cannot be undone.</p>
          <Dialog>
            <DialogTrigger asChild><Button className="mt-4" variant="danger"><Trash2 aria-hidden="true" className="h-4 w-4" />Delete Lead</Button></DialogTrigger>
            <DialogContent size="sm">
              <DialogHeader>
                <DialogTitle>Delete this Lead?</DialogTitle>
                <DialogDescription>This will permanently delete {lead.company_name}’s {LEAD_KIND_LABELS[lead.kind]} and no other Lead.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild><Button variant="ghost">Cancel</Button></DialogClose>
                <Button onClick={deleteLead} variant="danger">Delete only this Lead</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </section>
      </AdminShell>
    </>
  )
}

function confirmationDeliveryMessage(lead: AdminLeadDetail) {
  if (lead.consultation_confirmation_email_state === "sent") return "The confirmation email was sent."
  if (lead.consultation_confirmation_email_state === "pending") return "The confirmation email is queued."
  if (lead.consultation_confirmation_email_state === "uncertain") return "The delivery outcome needs manual review; automatic retries will not resend it."
  if (lead.consultation_confirmation_email_state === "delivering") return "A delivery attempt started and needs review if it does not complete; automatic retries will not resend it."

  return "No confirmation email delivery is recorded."
}

function Detail({ label, value, href, external = false }: { label: string; value: string; href?: string; external?: boolean }) {
  return (
    <div className="min-w-0 bg-page p-4">
      <dt><small>{label}</small></dt>
      <dd className="mt-1 break-words">
        {href ? <a href={href} rel={external ? "noreferrer" : undefined} target={external ? "_blank" : undefined}>{value}{external && <ExternalLink aria-hidden="true" className="ml-1 inline h-3 w-3" />}</a> : value}
      </dd>
    </div>
  )
}
